import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook to manage screen wake lock for shop mode
 * Prevents the screen from dimming while cutting
 */
export const useWakeLock = () => {
  const [isLocked, setIsLocked] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const wakeLock = useRef(null);

  useEffect(() => {
    setIsSupported('wakeLock' in navigator);
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) {
      console.warn('Wake Lock API not supported');
      return false;
    }

    try {
      wakeLock.current = await navigator.wakeLock.request('screen');
      setIsLocked(true);

      wakeLock.current.addEventListener('release', () => {
        setIsLocked(false);
      });

      console.log('Wake Lock acquired');
      return true;
    } catch (err) {
      console.error('Failed to acquire wake lock:', err);
      return false;
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLock.current) {
      try {
        await wakeLock.current.release();
        wakeLock.current = null;
        setIsLocked(false);
        console.log('Wake Lock released');
      } catch (err) {
        console.error('Failed to release wake lock:', err);
      }
    }
  }, []);

  const toggleWakeLock = useCallback(async () => {
    if (isLocked) {
      await releaseWakeLock();
    } else {
      await requestWakeLock();
    }
  }, [isLocked, requestWakeLock, releaseWakeLock]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (wakeLock.current) {
        wakeLock.current.release();
      }
    };
  }, []);

  // Re-acquire lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isLocked && !wakeLock.current) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLocked, requestWakeLock]);

  return {
    isLocked,
    isSupported,
    requestWakeLock,
    releaseWakeLock,
    toggleWakeLock,
  };
};

export default useWakeLock;
